import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Building2, Plus, Loader2, Mail, Phone, Globe, User, Package, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { money, priceUnitLabel, tagMeta } from "@/lib/helpers";
import { useAuth } from "@/context/AuthContext";
import TagControl from "@/components/TagControl";

const EMPTY = { company_name: "", contact_name: "", contact_email: "", contact_phone: "", website_url: "", types: [], category_id: null };
const SUPPLIER_TYPES = ["product", "equipment", "services"];
const NO_CAT = "__none__";

export default function Suppliers() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "manager";
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [s, p, c] = await Promise.all([api.get("/suppliers"), api.get("/products"), api.get("/supplier-categories")]);
    setSuppliers(s.data); setProducts(p.data); setCategories(c.data); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const catName = (id) => categories.find((c) => c.id === id)?.name;
  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => { setEditing(s.id); setForm({ company_name: s.company_name, contact_name: s.contact_name || "", contact_email: s.contact_email || "", contact_phone: s.contact_phone || "", website_url: s.website_url || "", types: s.types || [], category_id: s.category_id || null }); setOpen(true); };
  const toggleType = (t) => setForm((f) => ({ ...f, types: f.types.includes(t) ? f.types.filter((x) => x !== t) : [...f.types, t] }));

  const save = async () => {
    if (!form.company_name.trim()) return toast.error("Company name is required");
    setSaving(true);
    try {
      if (editing) await api.patch(`/suppliers/${editing}`, form);
      else await api.post("/suppliers", form);
      toast.success(editing ? "Contact updated" : "Contact added");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this contact? It will be unlinked from its products.")) return;
    try { await api.delete(`/suppliers/${id}`); toast.success("Contact deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveTag = async (id, tag, note) => {
    try { await api.post(`/suppliers/${id}/assessment`, { tag, note }); toast.success("Tag updated"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const productsFor = (sid) => products.filter((p) => p.supplier_id === sid);

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.15em] text-[#b32233]">Directory</p>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-900">Contacts</h2>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="add-supplier-button" onClick={openNew} className="flex items-center gap-2 bg-[#b32233] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#8f1a28]">
              <Plus className="h-4 w-4" /> New Contact
            </button>
          </DialogTrigger>
          <DialogContent className="rounded-none sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading tracking-tight">{editing ? "Edit contact" : "Add contact"}</DialogTitle>
              <DialogDescription>Store contact company and details.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <input data-testid="supplier-form-company" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Company name *" className="w-full border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#b32233]" />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="supplier-form-contact" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="Contact name" className="border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#b32233]" />
                <input data-testid="supplier-form-phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="Contact phone" className="border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#b32233]" />
                <input data-testid="supplier-form-email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="Contact email" className="border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#b32233]" />
                <input data-testid="supplier-form-website" value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} placeholder="Website URL" className="border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#b32233]" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Supplies</label>
                <div className="flex gap-2">
                  {SUPPLIER_TYPES.map((t) => (
                    <button key={t} type="button" data-testid={`supplier-type-${t}`} onClick={() => toggleType(t)} className={`border px-3 py-1.5 text-xs font-medium capitalize transition-colors ${form.types.includes(t) ? "border-[#b32233] bg-[#fdf0f1] text-[#b32233]" : "border-zinc-200 text-zinc-600 hover:border-zinc-300"}`}>{t}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Category</label>
                <Select value={form.category_id || NO_CAT} onValueChange={(v) => setForm({ ...form, category_id: v === NO_CAT ? null : v })}>
                  <SelectTrigger data-testid="supplier-form-category" className="rounded-none"><SelectValue placeholder="No category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_CAT}>No category</SelectItem>
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <button data-testid="supplier-form-submit" onClick={save} disabled={saving} className="flex items-center gap-2 bg-[#b32233] px-4 py-2 text-sm font-medium text-white hover:bg-[#8f1a28] disabled:opacity-60">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Save changes" : "Add contact"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#b32233]" /></div>
      ) : suppliers.length === 0 ? (
        <div className="flex h-64 items-center justify-center text-sm text-zinc-400">No contacts yet. Add your first one.</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {suppliers.map((s) => {
            const sp = productsFor(s.id);
            const tm = tagMeta(s.tag);
            return (
              <div key={s.id} className="border border-zinc-200 bg-white" data-testid={`supplier-card-${s.id}`}>
                <div className="flex items-start justify-between border-b border-zinc-200 p-4">
                  <div>
                    <p className="flex items-center gap-2 font-heading text-base font-semibold tracking-tight text-zinc-900">
                      <Building2 className="h-4 w-4 text-[#b32233]" /> {s.company_name}
                      {tm && <span className="border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider" style={{ color: tm.color, borderColor: tm.color, background: tm.bg }}>{tm.label}</span>}
                      {catName(s.category_id) && <span className="border border-[#b32233]/30 bg-[#fdf0f1] px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-[#b32233]" data-testid={`supplier-category-${s.id}`}>{catName(s.category_id)}</span>}
                      {(s.types || []).map((t) => <span key={t} className="border border-zinc-300 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-zinc-500" data-testid={`supplier-chip-${s.id}-${t}`}>{t}</span>)}
                    </p>
                    <div className="mt-2 space-y-1 text-xs text-zinc-500">
                      {s.contact_name && <p className="flex items-center gap-1.5"><User className="h-3 w-3" /> {s.contact_name}</p>}
                      {s.contact_email && <p className="flex items-center gap-1.5"><Mail className="h-3 w-3" /> {s.contact_email}</p>}
                      {s.contact_phone && <p className="flex items-center gap-1.5"><Phone className="h-3 w-3" /> {s.contact_phone}</p>}
                      {s.website_url && <a href={s.website_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-[#b32233] hover:underline"><Globe className="h-3 w-3" /> {s.website_url}</a>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEdit(s)} data-testid={`edit-supplier-${s.id}`} className="p-1 text-zinc-400 hover:text-[#b32233]"><Pencil className="h-4 w-4" /></button>
                    {canManage && <button onClick={() => del(s.id)} data-testid={`delete-supplier-${s.id}`} className="p-1 text-zinc-400 hover:text-[#b32233]"><Trash2 className="h-4 w-4" /></button>}
                  </div>
                </div>
                <div className="border-b border-zinc-200 p-4">
                  <p className="mb-2 text-xs font-medium uppercase tracking-wider text-zinc-500">Assessment</p>
                  <TagControl tag={s.tag} note={s.tag_note} onSave={(tag, note) => saveTag(s.id, tag, note)} testidPrefix={`supplier-tag-${s.id}`} />
                  {s.tag_note && (s.tag === "potential" || s.tag === "rejected") && <p className="mt-2 border-l-2 border-[#b32233] bg-zinc-50 px-2 py-1 text-xs text-zinc-600">{s.tag_note}</p>}
                </div>
                <div className="p-4">
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-zinc-500">
                    <Package className="h-3 w-3" /> Products sourced ({sp.length})
                  </p>
                  {sp.length === 0 ? (
                    <p className="text-xs text-zinc-400">No products from this contact yet</p>
                  ) : (
                    <div className="space-y-1">
                      {sp.map((p) => (
                        <div key={p.id} className="flex items-center justify-between border border-zinc-100 bg-zinc-50/50 px-2 py-1.5 text-sm">
                          <span className="text-zinc-800">{p.name}</span>
                          <span className="text-xs text-zinc-500">{p.rrp != null ? money(p.rrp) : (p.price != null ? `${money(p.price)} / ${priceUnitLabel(p.price_unit).replace("Per ", "")}` : "—")}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
